// Agents section: client-side filtering, copy-to-clipboard, and Markdown
// download. Runs only on pages that embed an #agents-data payload (the
// gallery and individual entry pages).

import { showToast as toast } from "./utils";

const initAgents = () => {
    const dataEl = document.getElementById("agents-data");
    if (!dataEl) {
        return;
    }

    let agents = [];
    try {
        agents = JSON.parse(dataEl.textContent || "[]");
    } catch (err) {
        console.error("Failed to parse agents data:", err);
        return;
    }

    const bySlug = new Map(agents.map((agent) => [agent.slug, agent]));

    const flashCopied = (button) => {
        const original = button.innerHTML;
        button.classList.add("copied");
        button.textContent = "Copied!";
        setTimeout(() => {
            button.classList.remove("copied");
            button.innerHTML = original;
        }, 1400);
    };

    const buildMarkdown = (agent) => {
        const tags = (agent.tags || []).join(", ");
        return (
            `# ${agent.title}\n\n` +
            `_${agent.desc}_\n\n` +
            `- **Type:** ${agent.type}\n` +
            `- **Category:** ${agent.cat}\n` +
            `- **Tags:** ${tags}\n\n` +
            "```\n" +
            `${agent.prompt}\n` +
            "```\n"
        );
    };

    // Copy
    document.querySelectorAll(".agent-copy").forEach((button) => {
        button.addEventListener("click", async () => {
            const agent = bySlug.get(button.dataset.slug);
            if (!agent) {
                return;
            }
            try {
                await navigator.clipboard.writeText(agent.prompt);
                flashCopied(button);
                toast("Copied to clipboard");
            } catch (err) {
                console.error("Failed to copy prompt:", err);
                toast("Couldn't copy — try again");
            }
        });
    });

    // Download
    document.querySelectorAll(".agent-download").forEach((button) => {
        button.addEventListener("click", () => {
            const agent = bySlug.get(button.dataset.slug);
            if (!agent) {
                return;
            }
            const blob = new Blob([buildMarkdown(agent)], {
                type: "text/markdown"
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${agent.slug}.md`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast(`Downloaded ${agent.slug}.md`);
        });
    });

    // Filtering (gallery only)
    const grid = document.getElementById("agent-grid");
    if (!grid) {
        return;
    }

    const searchInput = document.getElementById("agent-search");
    const emptyEl = document.getElementById("agent-empty");
    const cards = Array.from(grid.querySelectorAll(".agent-card"));
    const catSelect = document.getElementById("agent-cat");
    const tagSelect = document.getElementById("agent-tag");
    const featurePill = document.querySelector("[data-featured-toggle]");

    let query = "";
    let category = "all";
    let tag = "all";
    let featuredOnly = false;

    const matches = (card) => {
        if (featuredOnly && card.dataset.featured !== "true") {
            return false;
        }
        if (category !== "all" && card.dataset.cat !== category) {
            return false;
        }
        if (tag !== "all") {
            const agent = bySlug.get(card.dataset.slug) || {};
            if (!(agent.tags || []).includes(tag)) {
                return false;
            }
        }
        if (query) {
            const agent = bySlug.get(card.dataset.slug) || {};
            const haystack = [
                agent.title,
                agent.desc,
                agent.cat,
                (agent.tags || []).join(" ")
            ]
                .join(" ")
                .toLowerCase();
            if (!haystack.includes(query)) {
                return false;
            }
        }
        return true;
    };

    const apply = () => {
        let visible = 0;
        cards.forEach((card) => {
            const show = matches(card);
            card.style.display = show ? "" : "none";
            if (show) {
                visible += 1;
            }
        });
        if (emptyEl) {
            emptyEl.hidden = visible !== 0;
        }
    };

    if (searchInput) {
        searchInput.addEventListener("input", (event) => {
            query = event.target.value.trim().toLowerCase();
            apply();
        });
    }

    // Sync a <select>'s value and its wrapper's active state.
    const syncSelect = (select, value) => {
        if (!select) {
            return;
        }
        select.value = value;
        select.parentElement.classList.toggle("is-active", value !== "all");
    };

    // Reflect the current category/tag filters in the URL (no reload).
    const pushUrl = () => {
        const params = new URLSearchParams();
        if (category !== "all") {
            params.set("cat", category);
        }
        if (tag !== "all") {
            params.set("tag", tag);
        }
        const qs = params.toString();
        const url = qs ? `?${qs}` : location.pathname;
        history.pushState(null, "", url);
    };

    if (catSelect) {
        catSelect.addEventListener("change", (event) => {
            category = event.target.value;
            catSelect.parentElement.classList.toggle(
                "is-active",
                category !== "all"
            );
            pushUrl();
            apply();
        });
    }

    if (tagSelect) {
        tagSelect.addEventListener("change", (event) => {
            tag = event.target.value;
            tagSelect.parentElement.classList.toggle(
                "is-active",
                tag !== "all"
            );
            pushUrl();
            apply();
        });
    }

    if (featurePill) {
        featurePill.addEventListener("click", () => {
            featuredOnly = !featuredOnly;
            featurePill.classList.toggle("active", featuredOnly);
            apply();
        });
    }

    // Clicking a tag/category chip anywhere on the gallery filters in place.
    document.addEventListener("click", (event) => {
        const link = event.target.closest("a[data-tag], a[data-cat]");
        if (!link) {
            return;
        }
        event.preventDefault();
        if (link.dataset.tag !== undefined) {
            tag = link.dataset.tag;
            syncSelect(tagSelect, tag);
        } else {
            category = link.dataset.cat;
            syncSelect(catSelect, category);
        }
        pushUrl();
        apply();
    });

    // Apply filters carried in the URL on initial load.
    const applyFromUrl = () => {
        const params = new URLSearchParams(location.search);
        category = params.get("cat") || "all";
        tag = params.get("tag") || "all";
        syncSelect(catSelect, category);
        syncSelect(tagSelect, tag);
        apply();
    };

    applyFromUrl();
};

export { initAgents };
